import React, { useState, useEffect } from 'react';
import { Calendar, Database, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { toast } from 'react-toastify';

const AdvancedSyncForm = ({ onSyncComplete, paid }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [trial, setTrial] = useState(0);
  const [syncData, setSyncData] = useState({
    propertyId: '',
    startDate: '',
    endDate: '',
    email: localStorage.getItem('databackfill-useremail')
  });
  const [selectedPreset, setSelectedPreset] = useState('');
  const [currentPresetDetails, setCurrentPresetDetails] = useState(null);

  // All preset configurations with proper scope grouping
  const presetConfigs = [
    {
      name: "Basic Event Analysis",
      value: "basic_event",
      scope: "event",
      dimensions: ["eventName", "date", "platform", "deviceCategory"],
      metrics: ["eventCount", "activeUsers", "screenPageViews"]
    },
    {
      name: "User Demographics",
      value: "user_demographics", 
      scope: "event",
      dimensions: ["country", "city", "deviceCategory", "platform"],
      metrics: ["activeUsers", "screenPageViews", "eventCount"] 
    },
    {
      name: "Traffic Sources Analysis",
      value: "traffic_sources",
      scope: "session",
      dimensions: ["sessionSource", "sessionMedium", "sessionCampaignName"],
      metrics: ["sessions", "engagedSessions", "bounceRate"]
    },
    {
      name: "Page Performance",
      value: "page_performance",
      scope: "event", 
      dimensions: ["pagePath", "pageTitle", "deviceCategory"],
      metrics: ["screenPageViews", "eventCount", "conversions"]
    },
    {
      name: "E-commerce Tracking",
      value: "ecommerce",
      scope: "event",
      dimensions: ["eventName", "date", "country"],
      metrics: ["transactions", "purchaseRevenue", "eventCount"]
    },
    {
      name: "Session Acquisition",
      value: "session_acquisition",
      scope: "session",
      dimensions: ["sessionSource", "sessionMedium", "sessionCampaignName", "sessionDefaultChannelGroup"],
      metrics: ["sessions", "engagedSessions", "eventsPerSession", "engagementRate"]
    },
    {
      name: "User Acquisition",
      value: "user_acquisition",
      scope: "user",
      dimensions: ["firstUserSource", "firstUserMedium", "firstUserCampaignName"],
      metrics: ["totalUsers", "newUsers", "userEngagementDuration"]
    },
    {
      name: "Content Performance",
      value: "content_performance",
      scope: "event",
      dimensions: ["pagePath", "pageTitle", "country"],
      metrics: ["screenPageViews", "eventCount", "conversions"]
    },
    {
      name: "Event-based Ecommerce",
      value: "event_ecommerce",
      scope: "event",
      dimensions: ["eventName", "deviceCategory", "country"],
      metrics: ["transactions", "purchaseRevenue", "eventCount"]
    },
    {
      name: "Item-based Analysis", 
      value: "item_analysis",       
      scope: "event",
      dimensions: ["eventName", "date", "country"], 
      metrics: ["transactions", "purchaseRevenue", "conversions"]
    },
    {
      name: "Technical Performance",
      value: "technical_performance",
      scope: "event",
      dimensions: ["deviceCategory", "operatingSystem", "browser"],
      metrics: ["screenPageViews", "eventCount", "conversions"]
    },
    {
      name: "Conversion Funnel",
      value: "conversion_funnel",
      scope: "event",
      dimensions: ["eventName", "deviceCategory"],
      metrics: ["eventCount", "conversions"]
    }
  ];

  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    if (!paid) {
      // For free users: 3-day limit
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(today.getDate() - 3);
      const minDateStr = threeDaysAgo.toISOString().split('T')[0];
      
      // Start date constraints for free users
      const startDateEl = document.getElementById('start-date');
      if (startDateEl) {
        startDateEl.setAttribute('min', minDateStr);  // Can't go back more than 3 days
        startDateEl.setAttribute('max', todayStr);    // Can't pick future dates
      }
      
      // End date constraints for free users  
      const endDateEl = document.querySelector('input[name="endDate"]');
      if (endDateEl) {
        endDateEl.setAttribute('max', todayStr);      // Can't pick future dates
      }
    } else {
      // For paid users: no historical limit, but still can't pick future dates
      const startDateEl = document.getElementById('start-date');
      const endDateEl = document.querySelector('input[name="endDate"]');
      
      
      if (startDateEl) {
        startDateEl.removeAttribute('min');           // No historical limit
        startDateEl.setAttribute('max', todayStr);    // Can't pick future dates
      }
      
      if (endDateEl) {
        endDateEl.setAttribute('max', todayStr);      // Can't pick future dates
      }
    }
  
    // Calculate trial period (existing logic)
    const createdTime = localStorage.getItem('createdTime');
    if (createdTime) {
      const timestamp = parseInt(createdTime);
      const currentDate = new Date();
      const currentTimestamp = currentDate.getTime() / 1000;
      const differenceInSeconds = currentTimestamp - timestamp;
      const differenceInDays = Math.floor(differenceInSeconds / (60 * 60 * 24));
      setTrial(differenceInDays < 7 ? 7 - differenceInDays : 0);
    }
  }, [paid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSyncData((prev) => ({ ...prev, [name]: value }));
    
    // Handle start date changes to update end date constraints
    if (name === 'startDate' && value) {
      const endDateEl = document.querySelector('input[name="endDate"]');
      if (endDateEl) {
        endDateEl.setAttribute('min', value);
        
        // For free users, also limit end date to start date + 3 days
        if (!paid) {
          const startDate = new Date(value);
          const maxEndDate = new Date(startDate);
          maxEndDate.setDate(startDate.getDate() + 3);
          const today = new Date();
          
          // Use the earlier of (start + 3 days) or today
          const actualMaxEndDate = maxEndDate < today ? maxEndDate : today;
          const maxEndStr = actualMaxEndDate.toISOString().split('T')[0];
          
          endDateEl.setAttribute('max', maxEndStr);
        } else {
          const todayStr = new Date().toISOString().split('T')[0];
          endDateEl.setAttribute('max', todayStr);
        }
      }
    }
  };

  const handlePresetChange = (e) => {
    const presetValue = e.target.value;
    setSelectedPreset(presetValue);
    
    if (presetValue) {
      const preset = presetConfigs.find(p => p.value === presetValue);
      setCurrentPresetDetails(preset);
    } else {
      setCurrentPresetDetails(null);
    }
  };

  const validateDateRange = () => {
    if (!syncData.startDate || !syncData.endDate) {
      toast.error('Please select both start and end dates');
      return false;
    }
    
    const startDate = new Date(syncData.startDate);
    const endDate = new Date(syncData.endDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    // Check if dates are in the future
    if (startDate > today) {
      toast.error('Start date cannot be in the future');
      return false;
    }
    
    if (endDate > today) {
      toast.error('End date cannot be in the future');
      return false;
    }
    
    // Check if end date is before start date
    if (endDate < startDate) {
      toast.error('End date must be after or equal to start date');
      return false;
    }
    
    // For free users, check 3-day limit
    if (!paid) {
      const diffTime = Math.abs(endDate - startDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 3) {
        toast.error('Free tier is limited to 3 days of historical data. Please upgrade for longer ranges.');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    try {
      // Add this validation check at the very beginning
      if (!validateDateRange()) {
        setIsLoading(false);
        return;
      }
  
      // Basic validation
      if (!syncData.propertyId.trim()) {
        toast.error('Property ID is required');
        setIsLoading(false);
        return;
      }
  
      if (!selectedPreset) {
        toast.error('Please select a data configuration');
        setIsLoading(false);
        return;
      }
  
      // Rest of your existing submit logic stays the same...
      const preset = presetConfigs.find(p => p.value === selectedPreset);
      if (!preset) {
        toast.error('Invalid configuration selected');
        setIsLoading(false);
        return;
      }
  
      // Continue with your existing logic...
      const dimensions = [...preset.dimensions];
      if (!dimensions.includes('eventName')) dimensions.push('eventName');
      if (!dimensions.includes('date')) dimensions.push('date');
  
      const metrics = [...preset.metrics];
      if (!metrics.includes('eventCount')) metrics.push('eventCount');
  
      const result = await apiClient.post('/api/advanced-backfill', {
        property_id: syncData.propertyId,
        start_date: syncData.startDate,
        end_date: syncData.endDate,
        email: syncData.email,
        dimensions: dimensions,
        metrics: metrics
      });
  
      if (result.status === 'fill') {
        if (paid) {
          toast.error("You can't use more than 3 GA4 Properties");
        } else {
          toast.error("You can't use more than 1 GA4 Property");
        }
        setIsLoading(false);
        return;
      }
  
      toast.success('Data sync started successfully!');
      if (onSyncComplete) {
        onSyncComplete();
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(error.response?.data?.message || 'Failed to start sync');
    } finally {
      setIsLoading(false);
    }
  };

  // Scope color mapping
  const getScopeColor = (scope) => {
    switch(scope) {
      case 'user': return 'text-purple-600';
      case 'session': return 'text-blue-600';
      case 'event': return 'text-green-600';
      case 'item': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const getScopeBadgeColor = (scope) => {
    switch(scope) {
      case 'user': return 'bg-purple-100 text-purple-700';
      case 'session': return 'bg-blue-100 text-blue-700';
      case 'event': return 'bg-green-100 text-green-700';
      case 'item': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
<div className="bg-white rounded-lg shadow-sm max-w-3xl mx-auto">
<div className="p-6">
  <h2 className="text-xl font-semibold text-gray-800 mb-1">Data Sync</h2>
  <p className="text-sm text-gray-500 mb-6">Configure your GA4 data sync settings</p>

        <form onSubmit={handleSubmit}>
          {/* Property ID Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GA4 Property ID
            </label>
            <input
              type="text"
              name="propertyId"
              value={syncData.propertyId}
              onChange={handleChange}
              placeholder="Enter your GA4 Property ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                id="start-date"
                name="startDate"
                value={syncData.startDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={syncData.endDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Advanced Dimensions & Metrics Section */}
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-md font-medium mb-4 text-gray-800">Advanced Dimensions & Metrics</h3>
            
            {/* Preset Configuration Dropdown */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preset Configurations <span className="text-green-600 text-xs">(GA4 scope compatible)</span>
              </label>
              <select
                value={selectedPreset}
                onChange={handlePresetChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a compatible preset (recommended)</option>
                {presetConfigs.map(preset => (
                  <option key={preset.value} value={preset.value}>
                    {preset.name} ({preset.scope} scope)
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                These presets ensure dimension and metric compatibility based on GA4's scope hierarchy.
              </p>
            </div>

            {/* Show selected preset details */}
            {currentPresetDetails && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Current Selection Summary</h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getScopeBadgeColor(currentPresetDetails.scope)}`}>
                    {currentPresetDetails.scope} scope
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Selected Dimensions:</p>
                    <div className="flex flex-wrap gap-2">
                      {currentPresetDetails.dimensions.map(dim => (
                        <span key={dim} className={`text-xs px-2 py-1 rounded border ${getScopeBadgeColor(currentPresetDetails.scope)}`}>
                          {dim}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">Selected Metrics:</p>
                    <div className="flex flex-wrap gap-2">
                      {currentPresetDetails.metrics.map(metric => (
                        <span key={metric} className={`text-xs px-2 py-1 rounded border ${getScopeBadgeColor(currentPresetDetails.scope)}`}>
                          {metric}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* GA4 Data Scoping Guidelines */}
            <div className="mt-4 flex items-start bg-white p-3 rounded border border-blue-200">
              <AlertCircle size={16} className="text-blue-600 mt-0.5 mr-2 shrink-0" />
              <div className="text-xs text-gray-600">
                <p className="font-medium text-gray-700 mb-1">GA4 Data Scoping Guidelines:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Not all dimensions and metrics can be combined in a single report</li>
                  <li>User-level metrics (purple) can <span className="text-red-600">inflate numbers</span> when used with session or event dimensions</li>
                  <li>When working with item-level dimensions (orange), use only item metrics</li>
                  <li>For best results, use preset configurations to avoid scoping issues</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || (!paid && !trial)}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white
                    ${isLoading || (!paid && !trial)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                    } transition-colors`}
          >
            {isLoading ? 'Starting Sync...' : 'Start Sync'}
          </button>

          {/* Free Tier Notice */}
          {!paid && trial > 0 && (
            <div className="mt-4 bg-blue-50 p-3 rounded-lg text-xs text-gray-600 flex items-start">
              <AlertCircle size={16} className="text-blue-600 mt-0.5 mr-2 shrink-0" />
              <div>
                <p className="font-medium text-sm text-blue-800 mb-1">Free Tier Limitations:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Limited to backfilling 3 days of historical data</li>
                  <li>Only one GA4 property allowed</li>
                  <li>{trial} days remaining in your trial</li>
                </ul>
                <a href="/pricing" className="text-blue-600 hover:text-blue-800 font-medium mt-2 inline-block">
                  Upgrade for full access
                </a>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdvancedSyncForm;